import { Chip, Tooltip } from "@mui/material";
import { useTheme, darken } from "@mui/material/styles";

export type HarshEventStatus =
  | "pending"
  | "completed"
  | "no answer"
  | "pending (api)"
  | "completed (api)"
  | "noanswer (api)"
  | "null";

export const harshEventStatusColors: Record<
  Exclude<
    HarshEventStatus,
    "pending (api)" | "completed (api)" | "noanswer (api)"
  >,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  pending: { label: "Pending", color: "warning" },
  completed: { label: "Completed", color: "success" },
  "no answer": { label: "No Answer", color: "error" },
  null: { label: "No Status", color: "default" },
};

const HarshEventStatusChip = ({ value }: { value?: string | null }) => {
  const theme = useTheme();

  const normalized = (value || "").toString();
  let lookupKey: HarshEventStatus = "null";
  switch (normalized) {
    case "Pending":
      lookupKey = "pending";
      break;
    case "Completed":
      lookupKey = "completed";
      break;
    case "NoAnswer":
      lookupKey = "no answer";
      break;
    default: {
      const lower = normalized.toLowerCase();
      if (lower === "pending") lookupKey = "pending";
      else if (lower === "completed") lookupKey = "completed";
      else if (lower === "no answer" || lower === "noanswer")
        lookupKey = "no answer";
      else lookupKey = "null";
    }
  }

  const mapping = harshEventStatusColors[lookupKey];
  const label = mapping?.label || (value ? value : "No Status");
  const color = mapping?.color || "default";

  let baseColor: string;
  switch (color) {
    case "primary":
      baseColor = theme.palette.primary.main;
      break;
    case "secondary":
      baseColor = theme.palette.secondary.main;
      break;
    case "error":
      baseColor = theme.palette.error.main;
      break;
    case "info":
      baseColor = theme.palette.info.main;
      break;
    case "success":
      baseColor = theme.palette.success.main;
      break;
    case "warning":
      baseColor = theme.palette.warning.main;
      break;
    default:
      baseColor = theme.palette.grey[500];
      break;
  }

  const chipStyle = {
    backgroundColor: darken(baseColor, 0.0),
    color: "#fff",
  } as const;

  return (
    <Tooltip title={label}>
      <Chip size="small" label={label} style={chipStyle} />
    </Tooltip>
  );
};

export default HarshEventStatusChip;
